// @ts-check

/**
 * @file Renders the project board task create and edit modal.
 */

import {Form} from "@kaspernj/api-maker/build/form"
import FontAwesome6 from "@expo/vector-icons/FontAwesome6"
import _ from "gettext-universal/build/src/translate.js"
import useLocale from "gettext-universal/build/src/use-locale-expo.js"
import {createRef, memo, useEffect} from "react"
import PropTypes from "prop-types"
import PropTypesExact from "prop-types-exact"
import {Pressable, ScrollView, View} from "react-native-propforge"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component.js"
import Button from "../../../components/button"
import FormHtmlEditor from "../../../components/form/form-html-editor"
import {FormHtmlEditorUploadSession} from "../../../components/form/form-html-editor/upload-session"
import FormSelect from "../../../components/form/form-select"
import Modal from "../../../components/modal"
import {queueOfflineTaskCreate, queueOfflineTaskEdit} from "../../../database/project-replica-task-commands"
import errorMessage from "../../../shared/error-message"
import FormTextInput from "../../../components/form/form-text-input"
import {Task} from "../../../frontend-models/task"
import {TaskBoard} from "../../../frontend-models/task-board"
import Text from "@kaspernj/api-maker/build/utils/text"
import useThemeColors, {useThemeName} from "../../../shared/use-theme-colors"

/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @import {OpenProjectReplica} from "../../../database/project-replica.js" */
/** @type {Record<string, object>} */
const styles = {}
const PRIORITY_OPTIONS = [1, 2, 3, 4, 5]

/** @typedef {{id: string, name: string}} ProjectBoardNewTaskModalBoardColumnOption */
/** @typedef {{defaultForProject: boolean, id: string, name: string}} ProjectBoardNewTaskModalTaskTypeOption */
/** @typedef {(...args: unknown[]) => unknown} ProjectBoardNewTaskModalCallback */
/** @typedef {{[key: string]: unknown, allowTaskTypeChange?: boolean, boardColumnOptions: ProjectBoardNewTaskModalBoardColumnOption[], boardId?: string | null, boardProjectId?: string, columnId?: string, currentUserId?: string | null, initialColumnId?: string, isVisible: boolean, onClosePress: ProjectBoardNewTaskModalCallback, onCreated?: ProjectBoardNewTaskModalCallback, onUpdated?: ProjectBoardNewTaskModalCallback, online?: boolean, replica?: OpenProjectReplica | null, task?: Task | null, taskBoardsCount: number, taskTypeOptions: ProjectBoardNewTaskModalTaskTypeOption[]}} ProjectBoardNewTaskModalProps */

/**
 * @typedef {object} ProjectBoardNewTaskModalState
 * @property {string} errorText Validation error text.
 * @property {string} modalDefaultsKey Last modal context that applied default selections.
 * @property {boolean} saving Whether the task is saving.
 * @property {string | null} selectedBoardColumnId Column receiving the task.
 * @property {number} selectedPriority Selected task priority.
 * @property {string | null} selectedTaskTypeId Type applied to the task.
 */

/**
 * Project board task create/edit modal.
 * @augments {ShapeComponent<ProjectBoardNewTaskModalProps, ProjectBoardNewTaskModalState>}
 */
class ProjectBoardNewTaskModal extends ShapeComponent {
  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()
  descriptionUploadSession = new FormHtmlEditorUploadSession()
  formObjectRef = createRef()
  static propTypes = PropTypesExact({
    allowTaskTypeChange: PropTypes.bool,
    boardColumnOptions: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired
      }).isRequired
    ).isRequired,
    boardId: PropTypes.string,
    boardProjectId: PropTypes.string,
    columnId: PropTypes.string,
    currentUserId: PropTypes.string,
    initialColumnId: PropTypes.string,
    isVisible: PropTypes.bool.isRequired,
    onClosePress: PropTypes.func.isRequired,
    onCreated: PropTypes.func,
    onUpdated: PropTypes.func,
    online: PropTypes.bool,
    replica: PropTypes.object,
    task: PropTypes.object,
    taskBoardsCount: PropTypes.number.isRequired,
    taskTypeOptions: PropTypes.arrayOf(
      PropTypes.shape({
        defaultForProject: PropTypes.bool.isRequired,
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired
      }).isRequired
    ).isRequired
  })
  static defaultProps = {
    allowTaskTypeChange: true,
    boardId: null,
    boardProjectId: undefined,
    columnId: undefined,
    initialColumnId: undefined,
    onCreated: undefined,
    onUpdated: undefined,
    online: true,
    replica: null,
    task: null
  }
  /** @type {ProjectBoardNewTaskModalState} */
  state = {
    errorText: "",
    modalDefaultsKey: "",
    saving: false,
    selectedBoardColumnId: /** @type {string | null} */ null,
    selectedPriority: 1,
    selectedTaskTypeId: /** @type {string | null} */ null
  }

  /** Initializes modal defaults when it opens. */
  setup() {
    useLocale()
    this.colors = useThemeColors()
    this.themeName = useThemeName()
    const boardProjectId = this.p.boardProjectId
    const isVisible = this.p.isVisible
    /** @type {Task | null} */
    const task = this.p.task
    const taskTypeOptions = this.p.taskTypeOptions
    const initialBoardColumnId =
      this.p.initialColumnId || this.p.columnId || null
    const initialPriority = task?.priority() || 1
    const initialTaskTypeId = initialTaskTypeIdForTask(task, taskTypeOptions)
    const modalDefaultsKey = modalDefaultsKeyForTaskModal({
      boardProjectId,
      initialBoardColumnId,
      initialPriority,
      initialTaskTypeId,
      isVisible,
      task
    })

    if (this.state.modalDefaultsKey !== modalDefaultsKey) {
      this.state.errorText = ""
      this.state.modalDefaultsKey = modalDefaultsKey
      this.state.saving = false
      this.state.selectedBoardColumnId = isVisible ? initialBoardColumnId : null
      this.state.selectedPriority = isVisible ? initialPriority : 1
      this.state.selectedTaskTypeId = isVisible ? initialTaskTypeId : null
    }
    useEffect(() => {
      if (!isVisible) return
      this.tt.descriptionUploadSession.reset()
    }, [isVisible, modalDefaultsKey])
  }

  /**
   * Renders the task modal.
   * @returns {ReactNode} Modal content.
   */
  render() {
    /** @type {Task | null} */
    const task = this.p.task
    const themeName = this.tt.themeName
    const colors = this.tt.colors
    const initialDescription = task?.description() || ""
    const initialName = task?.name() || ""
    const initialTaskTypeId = initialTaskTypeIdForTask(task, this.p.taskTypeOptions)
    return (
      <Modal
        cardStyle={styles[`card-${themeName}`] ||= {
          gap: 16,
          maxHeight: "90%",
          maxWidth: 560,
          overflow: "hidden",
          padding: 20
        }}
        isVisible={this.p.isVisible}
        onRequestClose={this.p.onClosePress}
        testID="projectBoardNewTaskModal"
      >
          <Text
            style={styles.title ||= {
              fontSize: 20,
              fontWeight: 700
            }}
            testID="projectBoardNewTaskModal/title"
          >
            {task ? _("Edit task") : _("Create New Task")}
          </Text>
          <Form
            formObjectRef={this.formObjectRef}
            onSubmit={this.tt.onSavePress}
          >
            <ScrollView
              contentContainerStyle={styles.formScrollContent ||= {
                gap: 12,
                paddingBottom: 2
              }}
              keyboardShouldPersistTaps="handled"
              style={styles.formScroll ||= {
                flexShrink: 1
              }}
              testID="projectBoardNewTaskModal/scroll"
            >
            <View
              style={styles.field ||= {
                gap: 6
              }}
              testID="projectBoardNewTaskModal/nameField"
            >
              <FormTextInput
                defaultValue={initialName}
                label={_("Name")}
                name="name"
                testID="projectBoardNewTaskModal/nameInput"
              />
            </View>
            <View
              style={styles.field}
              testID="projectBoardNewTaskModal/descriptionField"
            >
              <FormHtmlEditor
                autoGrowMaxHeight={320}
                defaultValue={initialDescription}
                label={_("Description")}
                name="description"
                resizable
                testID="projectBoardNewTaskModal/descriptionInput"
                uploadSession={this.p.online ? this.tt.descriptionUploadSession : undefined}
              />
            </View>
            <View
              style={styles.optionsGrid ||= {
                flexDirection: "row",
                flexWrap: "wrap",
                gap: 16
              }}
              testID="projectBoardNewTaskModal/optionsGrid"
            >
              <View
                style={styles.optionSection ||= {
                  flexBasis: "100%",
                  gap: 8
                }}
                testID="projectBoardNewTaskModal/columnSection"
              >
                <Text
                  style={styles[`label-${themeName}`] ||= {
                    color: colors.textMuted,
                    fontSize: 12,
                    fontWeight: 700,
                    textTransform: "uppercase"
                  }}
                  testID="projectBoardNewTaskModal/columnLabel"
                >
                  {_("Column")}
                </Text>
                <View
                  style={styles.optionRow ||= {
                    flexDirection: "row",
                    flexWrap: "wrap",
                    gap: 8
                  }}
                  testID="projectBoardNewTaskModal/columnOptions"
                >
                  {this.p.boardColumnOptions.map((boardColumnOption) => {
                    const active = boardColumnOption.id === this.s.selectedBoardColumnId
                    return (
                      <Pressable
                        key={boardColumnOption.id}
                        onPress={() =>
                          this.tt.onBoardColumnPress(boardColumnOption.id)
                        }
                        style={styles[`chip-${themeName}-${active}`] ||= {
                          alignItems: "center",
                          backgroundColor: active ? colors.infoBackground : colors.surface,
                          borderColor: active ? colors.info : colors.border,
                          borderRadius: 999,
                          borderWidth: 1,
                          flexDirection: "row",
                          paddingHorizontal: 12,
                          paddingVertical: 8
                        }}
                        testID={`projectBoardNewTaskModal/column/${boardColumnOption.id}`}
                      >
                        <View
                          style={styles.chipIcon ||= {
                            marginRight: 8,
                            width: 14
                          }}
                          testID={`projectBoardNewTaskModal/column/${boardColumnOption.id}/icon`}
                        >
                          <FontAwesome6 color={active ? colors.link : colors.text} name="table-columns" size={13} />
                        </View>
                        <Text
                          style={styles[`chipText-${themeName}-${active}`] ||= {
                            color: active ? colors.link : colors.text,
                            fontSize: 13,
                            fontWeight: 600
                          }}
                          testID={`projectBoardNewTaskModal/column/${boardColumnOption.id}/label`}
                        >
                          {boardColumnOption.name}
                        </Text>
                      </Pressable>
                    )
                  })}
                </View>
              </View>
              {this.p.allowTaskTypeChange &&
                <View
                  style={styles.optionSection}
                  testID="projectBoardNewTaskModal/taskTypeSection"
                >
                  <View
                    testID="projectBoardNewTaskModal/taskTypeOptions"
                  >
                    <FormSelect
                      defaultValue={initialTaskTypeId ? String(initialTaskTypeId) : ""}
                      label={_("Task type")}
                      name="taskTypeId"
                      onChangeValue={this.tt.onTaskTypeChangeValue}
                      options={this.p.taskTypeOptions.map((taskTypeOption) => ({
                        text: taskTypeOption.name,
                        value: String(taskTypeOption.id)
                      }))}
                      placeholder={_("No task type")}
                      search
                      testID="projectBoardNewTaskModal/taskTypeInput"
                    />
                  </View>
                </View>
              }
              <View
                style={styles.optionSection}
                testID="projectBoardNewTaskModal/prioritySection"
              >
                <Text
                  style={styles[`label-${themeName}`]}
                  testID="projectBoardNewTaskModal/priorityLabel"
                >
                  {_("Priority")}
                </Text>
                <View
                  style={styles.optionRow}
                  testID="projectBoardNewTaskModal/priorityOptions"
                >
                  {PRIORITY_OPTIONS.map((priorityValue) => {
                    const active = priorityValue === this.s.selectedPriority
                    return (
                      <Pressable
                        key={priorityValue}
                        onPress={() => this.tt.onPriorityPress(priorityValue)}
                        style={styles[`chip-${themeName}-${active}`] ||= {
                          alignItems: "center",
                          backgroundColor: active ? colors.infoBackground : colors.surface,
                          borderColor: active ? colors.info : colors.border,
                          borderRadius: 999,
                          borderWidth: 1,
                          flexDirection: "row",
                          paddingHorizontal: 12,
                          paddingVertical: 8
                        }}
                        testID={`projectBoardNewTaskModal/priority/${priorityValue}`}
                      >
                        <View
                          style={styles.chipIcon}
                          testID={`projectBoardNewTaskModal/priority/${priorityValue}/icon`}
                        >
                          <FontAwesome6 color={active ? colors.link : colors.text} name="flag" size={13} />
                        </View>
                        <Text
                          style={styles[`chipText-${themeName}-${active}`] ||= {
                            color: active ? colors.link : colors.text,
                            fontSize: 13,
                            fontWeight: 600
                          }}
                          testID={`projectBoardNewTaskModal/priority/${priorityValue}/label`}
                        >
                          {String(priorityValue)}
                        </Text>
                      </Pressable>
                    )
                  })}
                </View>
              </View>
            </View>
            {this.s.errorText !== "" &&
              <View
                style={styles[`errorCard-${themeName}`] ||= {
                  backgroundColor: colors.dangerBackground,
                  borderColor: colors.danger,
                  borderRadius: 12,
                  borderWidth: 1,
                  padding: 12
                }}
                testID="projectBoardNewTaskModal/error"
              >
                <Text
                  style={styles[`errorText-${themeName}`] ||= {
                    color: colors.dangerText,
                    fontSize: 14,
                    fontWeight: 600
                  }}
                  testID="projectBoardNewTaskModal/errorText"
                >
                  {this.s.errorText}
                </Text>
              </View>
            }
            </ScrollView>
            <View
              style={styles.actions ||= {
                flexDirection: "row",
                gap: 10,
                justifyContent: "flex-end",
                marginTop: 16
              }}
              testID="projectBoardNewTaskModal/actions"
            >
              <Button
                iconName="xmark"
                label={_("Cancel")}
                onPress={this.p.onClosePress}
                testID="projectBoardNewTaskModal/cancelButton"
                variant="outline"
              />
              <Button
                disabled={this.s.saving}
                iconName="floppy-disk"
                label={
                  this.s.saving
                    ? this.p.task
                      ? _("Saving...")
                      : _("Creating...")
                    : this.p.task
                      ? _("Save")
                      : _("Create")
                }
                onPress={this.tt.onSavePress}
                testID="projectBoardNewTaskModal/createButton"
              />
            </View>
          </Form>
      </Modal>
    )
  }

  /**
   * Applies the selected priority.
   * @param {number} priorityValue Priority value to select.
   * @returns {void} Nothing.
   */
  onPriorityPress = (priorityValue) => {
    this.s.errorText = ""
    this.s.selectedPriority = priorityValue
  }

  /**
   * Applies the selected board column.
   * @param {string} boardColumnId Board column id to select.
   * @returns {void} Nothing.
   */
  onBoardColumnPress = (boardColumnId) => {
    this.s.errorText = ""
    this.s.selectedBoardColumnId = boardColumnId
  }

  /**
   * Applies the selected task type from the shared select.
   * @param {(string | number)[] | string | number | undefined} value Select value.
   * @returns {void} Nothing.
   */
  onTaskTypeChangeValue = (value) => {
    let taskTypeId = null
    if (Array.isArray(value) && value.length > 0) {
      taskTypeId = String(value[0])
    } else if (typeof value === "string" || typeof value === "number") {
      taskTypeId = String(value)
    }
    this.s.errorText = ""
    this.s.selectedTaskTypeId = Number.isNaN(taskTypeId) ? null : taskTypeId
  }

  /**
   * Saves a new or existing task from the form.
   * @returns {Promise<void>} Resolves when save handling completes.
   * @rejects {Error} When form wiring is missing.
   */
  onSavePress = async () => {
    const boardProjectId = this.p.boardProjectId
    const selectedBoardColumnId = this.s.selectedBoardColumnId
    const formObject = this.formObjectRef.current
    /** @type {Task | null} */
    const task = this.p.task
    if (!formObject) {
      throw new Error("ProjectBoardNewTaskModal requires an Api Maker Form")
    }
    const values = formObject.asObject()
    const rawDescription = String(values.description || "")
    const trimmedName = String(values.name || "").trim()
    const selectedTaskTypeId = this.p.allowTaskTypeChange
      ? values.taskTypeId
        ? String(values.taskTypeId)
        : this.s.selectedTaskTypeId
      : task?.taskTypeId() || null
    if (!trimmedName) {
      this.s.errorText = _("Please enter a title.")
      return
    }
    if (!boardProjectId || !selectedBoardColumnId) {
      this.s.errorText = _("Board not found.")
      return
    }
    if (this.p.allowTaskTypeChange && !selectedTaskTypeId) {
      this.s.errorText = _("Choose a task type.")
      return
    }
    this.s.errorText = ""
    this.s.saving = true
    /** @type {Task | null} */
    let createdTask = null
    try {
      const currentUserId = this.p.currentUserId || null
      const uploadedFilesAttributes = this.tt.descriptionUploadSession.nestedUploadedFilesAttributes(rawDescription, currentUserId)
      const descriptionForSave = uploadedFilesAttributes.length > 0
        ? rawDescription
        : this.tt.descriptionUploadSession.flushNestedUploadedFilesHtml({html: rawDescription, uploadedFiles: []})
      const nextDescriptionForSave = descriptionForSave.trim() ? descriptionForSave : null

      if (!this.p.online) {
        const replica = this.p.replica
        if (!replica) {
          this.s.errorText = _("This task can't be saved until the project is available.")
          this.s.saving = false
          return
        }
        if (uploadedFilesAttributes.length > 0) {
          this.s.errorText = _("Attachments can't be saved while you're offline.")
          this.s.saving = false
          return
        }
        if (task) {
          await queueOfflineTaskEdit({
            changes: {
              description: nextDescriptionForSave,
              name: trimmedName,
              priority: this.s.selectedPriority,
              ...(this.p.allowTaskTypeChange ? {taskTypeId: selectedTaskTypeId} : {})
            },
            replica,
            taskId: task.id()
          })
          if (this.p.onUpdated) {
            await this.p.onUpdated(selectedBoardColumnId, true)
          }
        } else {
          if (!currentUserId) {
            throw new Error("ProjectBoardNewTaskModal requires currentUserId before creating a task")
          }
          const boardId = this.p.boardId
          if (!boardId) {
            throw new Error("ProjectBoardNewTaskModal requires boardId before creating a task offline")
          }
          await queueOfflineTaskCreate({
            boardColumnId: selectedBoardColumnId,
            boardId,
            description: nextDescriptionForSave,
            name: trimmedName,
            priority: this.s.selectedPriority,
            projectId: boardProjectId,
            replica,
            taskTypeId: selectedTaskTypeId,
            userId: currentUserId
          })
          this.p.onClosePress()
        }
        return
      }

      if (task) {
        const taskChanged =
          task.description() !== nextDescriptionForSave ||
          task.name() !== trimmedName ||
          task.priority() !== this.s.selectedPriority ||
          (this.p.allowTaskTypeChange && task.taskTypeId() !== selectedTaskTypeId) ||
          uploadedFilesAttributes.length > 0
        /** @type {Task} */
        let savedTask = task

        if (taskChanged) {
          savedTask = await task.update({
            description: nextDescriptionForSave,
            name: trimmedName,
            priority: this.s.selectedPriority,
            ...(this.p.allowTaskTypeChange ? {taskTypeId: selectedTaskTypeId} : {}),
            ...(uploadedFilesAttributes.length > 0 ? {uploadedFilesAttributes} : {})
          })
        }
        if (uploadedFilesAttributes.length > 0) {
          const uploadedDescription = this.tt.descriptionUploadSession.flushNestedUploadedFilesHtml({
            html: rawDescription,
            uploadedFiles: savedTask.uploadedFilesLoaded()
          })
          const nextUploadedDescription = uploadedDescription.trim() ? uploadedDescription : null

          if (nextUploadedDescription !== savedTask.description()) {
            await savedTask.update({description: nextUploadedDescription})
          }
        }
        if (this.p.onUpdated) {
          await this.p.onUpdated(selectedBoardColumnId, taskChanged)
        }
        return
      }

      if (!currentUserId) {
        throw new Error("ProjectBoardNewTaskModal requires currentUserId before creating a task")
      }

      createdTask = await Task.create({
        description: nextDescriptionForSave,
        name: trimmedName,
        priority: this.s.selectedPriority,
        projectId: boardProjectId,
        taskTypeId: selectedTaskTypeId,
        userId: currentUserId,
        ...(uploadedFilesAttributes.length > 0 ? {uploadedFilesAttributes} : {})
      })

      if (uploadedFilesAttributes.length > 0) {
        const uploadedDescription = this.tt.descriptionUploadSession.flushNestedUploadedFilesHtml({
          html: rawDescription,
          uploadedFiles: createdTask.uploadedFilesLoaded()
        })
        const nextUploadedDescription = uploadedDescription.trim() ? uploadedDescription : null

        if (nextUploadedDescription !== createdTask.description()) {
          await createdTask.update({description: nextUploadedDescription})
        }
      }
      const placeResponse = await TaskBoard.place({
        boardColumnId: selectedBoardColumnId,
        taskId: createdTask.id()
      })
      const taskBoard = placeResponse.taskBoard
      if (this.p.onCreated) {
        await this.p.onCreated(taskBoard)
      }
    } catch (error) {
      const originalErrorMessage = errorMessage(error)
      const shouldRethrow = !isValidationTaskSaveError(error, originalErrorMessage)

      if (createdTask?.id()) {
        try {
          await createdTask.destroy()
        } catch (_destroyError) {
          // Keep the original create error visible to the user.
        }
      }
      this.s.errorText = originalErrorMessage
      this.s.saving = false

      if (shouldRethrow) {
        throw error
      }
    }
  }
}

/**
 * Whether a task save error is validation-style feedback handled by the modal.
 * @param {unknown} error Error thrown while saving.
 * @param {string} message Display message for the error.
 * @returns {boolean} Whether the modal should handle the error without rethrowing.
 */
function isValidationTaskSaveError(error, message) {
  if (!(error instanceof Error)) return false
  const taskSaveError = /** @type {Error & {errorType?: unknown, validationErrors?: unknown, velocious?: {code?: unknown}}} */ (error)

  if (taskSaveError.errorType === "validation_error") return true
  if (taskSaveError.validationErrors && typeof taskSaveError.validationErrors === "object") return true
  if (taskSaveError.velocious?.code === "task-type-project-mismatch") return true
  if (taskSaveError.velocious?.code === "form-html-editor-upload-failed") return true

  return false
}

/** @type {NamedExoticComponent<ProjectBoardNewTaskModalProps>} */
const ProjectBoardNewTaskModalComponent = memo(shapeComponent(ProjectBoardNewTaskModal))

export default ProjectBoardNewTaskModalComponent

/**
 * Finds the initial task type for a new or existing board task.
 * @param {Task | null} task Existing task.
 * @param {ProjectBoardNewTaskModalTaskTypeOption[]} taskTypeOptions Available task type options.
 * @returns {string | null} Selected task type id.
 */
function initialTaskTypeIdForTask(task, taskTypeOptions) {
  const defaultTaskType = taskTypeOptions.find((taskTypeOption) => taskTypeOption.defaultForProject)

  return task?.taskTypeId() || defaultTaskType?.id || taskTypeOptions[0]?.id || null
}

/**
 * Builds a key for applying default selections only when the modal context changes.
 * @param {{boardProjectId: string | undefined, initialBoardColumnId: string | null, initialPriority: number, initialTaskTypeId: string | null, isVisible: boolean, task: Task | null}} args Modal default inputs.
 * @returns {string} Modal default context key.
 */
function modalDefaultsKeyForTaskModal({boardProjectId, initialBoardColumnId, initialPriority, initialTaskTypeId, isVisible, task}) {
  if (!isVisible) return "closed"

  return [
    boardProjectId || "no-project",
    task ? `task-${task.id()}` : "new-task",
    initialBoardColumnId === null ? "no-column" : `column-${initialBoardColumnId}`,
    `priority-${initialPriority}`,
    initialTaskTypeId === null ? "no-task-type" : `task-type-${initialTaskTypeId}`
  ].join("|")
}
